import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useIsMutating } from "@tanstack/react-query";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import clsx from "clsx";

import {
  CategoriesModel,
  CategorySelectorModel,
  TransactionFormInterface,
  AttachmentModel,
  PeriodModel,
  AssetModel,
  AssetType,
  ApiErrorModel,
  TransactionBulkSharedValues,
} from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import {
  QUERY_WIDTHS,
  transactionSchema,
  formatAmountAfterEditing,
  formatAmountOnBlur,
  getSubcategoryById,
  filterAssetsByDate,
  getAmountLimitViolation,
} from "@utils/index";
import {
  useTransactionGetOne,
  useTransactionGetCategories,
  useAssetGetBusinessAssets,
  useTransactionCreate,
  useTransactionUpdate,
  useTransactionFindPeriod,
} from "@providers/index";
import { useBusinessType, useFileUpload, useNotify } from "@hooks/index";
import {
  MobileHeader,
  ChevronLeftSvg,
  Form,
  FormRow,
  CategorySelector,
  DatePicker,
  FormInput,
  FormText,
  Button,
  SquareRoundedPlusSvg,
  DeviceFloppySvg,
  CheckupListSvg,
  XSvg,
  BusinessSelector,
  ConfirmModal,
  AssetSelector,
  TransactionBulkDetails,
  TransactionBulkDetailsHandle,
  TransactionReceipts,
  DuplicatedTransactionModal,
  AssetRequiredModal,
  WrongAssetTypeModal,
  HardMaxAmountModal,
  SoftAmountLimitModal,
} from "@components/index";
import { AssetForm } from "@pages/index";

export const TransactionForm = ({
  isRefundForm,
}: {
  isRefundForm?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const idValue = location.pathname.split("/").slice(-1)[0];
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });

  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );
  const businesses = useUserStore(({ businesses }) => businesses);
  const defaultBusiness = useUserStore(({ business }) => business);
  const [businessId, setBusinessId] = useState("");
  const businessType = useBusinessType(businessId);

  const [categories, setCategories] = useState<CategoriesModel>();
  const [category, setCategory] = useState<CategorySelectorModel>();
  const [excludedAssetTypes, setExcludedAssetTypes] = useState<AssetType[]>();

  const [date, setDate] = useState<Date | undefined>(new Date());
  const [period, setPeriod] = useState<PeriodModel>();

  const [assetId, setAssetId] = useState("");
  const [assets, setAssets] = useState<AssetModel[]>();
  const [previousDate, setPreviousDate] = useState<Date>();

  const [attachments, setAttachments] = useState<AttachmentModel[]>([]);

  const bulkDetailsRef = useRef<TransactionBulkDetailsHandle>(null);
  const [bulkCount, setBulkCount] = useState(1);

  const [confirmBiggerAmount, setConfirmBiggerAmount] = useState(false);

  const [modalDuplicated, setModalDuplicated] = useState(false);
  const [modalAssetError, setModalAssetError] = useState(false);
  const [modalWrongAsetType, setModalWrongAsetType] = useState(false);
  const [limitViolation, setLimitViolation] =
    useState<ReturnType<typeof getAmountLimitViolation>>();
  const [confirmSoftMaxAmount, setConfirmSoftMaxAmount] = useState(false);
  const [confirmSoftMinAmount, setConfirmSoftMinAmount] = useState(false);

  const subcategory = useMemo(
    () => getSubcategoryById(categories, category?.categoryId),
    [categories, category],
  );

  const isBulkMode =
    idValue === "create" &&
    !isRefundForm &&
    businessType === "driving_instructor" &&
    !!import.meta.env.VITE_BULK_INCOME_CATEGORY_ID &&
    category?.categoryId === import.meta.env.VITE_BULK_INCOME_CATEGORY_ID;
  const isPendingBulkCreate =
    useIsMutating({ mutationKey: ["transactionBulkCreate"] }) > 0;

  const dateStr = useMemo(() => {
    if (!date) {
      return "";
    }

    let dateStr = `${date.getFullYear()}`;
    dateStr += "-";
    dateStr += `0${date.getMonth() + 1}`.slice(-2);
    dateStr += "-";
    return (dateStr += `0${date.getDate()}`.slice(-2));
  }, [date]);

  const {
    isPending: isPendingGetOne,
    isSuccess: isSuccessGetOne,
    isStale: isStaleGetOne,
    data: dataGetOne,
    isError: isErrorGetOne,
    error: errorGetOne,
  } = useTransactionGetOne(
    ["refund", "create"].includes(idValue) ? "" : idValue,
  );

  useEffect(() => {
    if (isErrorGetOne && errorGetOne) {
      notify("error", errorGetOne.message);
    }
  }, [isErrorGetOne, errorGetOne, notify]);

  const {
    isPending: isPendingGetCategories,
    isSuccess: isSuccessGetCategories,
    data: dataGetCategories,
    isError: isErrorGetCategories,
    error: errorGetCategories,
  } = useTransactionGetCategories(businessType, true);

  useEffect(() => {
    if (isSuccessGetCategories && dataGetCategories) {
      setCategories(dataGetCategories);
    }
  }, [isSuccessGetCategories, dataGetCategories]);

  useEffect(() => {
    if (isErrorGetCategories && errorGetCategories) {
      notify("error", errorGetCategories.message);
    }
  }, [isErrorGetCategories, errorGetCategories, notify]);

  const {
    isPending: isPendingGetAssets,
    isSuccess: isSuccessGetAssets,
    data: dataGetAssets,
    isError: isErrorGetAssets,
    error: errorGetAssets,
  } = useAssetGetBusinessAssets(businessId);

  useEffect(() => {
    if (isSuccessGetAssets && dataGetAssets) {
      setAssets(dataGetAssets);
    }
  }, [isSuccessGetAssets, dataGetAssets]);

  useEffect(() => {
    if (isErrorGetAssets && errorGetAssets) {
      notify("error", errorGetAssets.message);
    }
  }, [isErrorGetAssets, errorGetAssets, notify]);

  const {
    isPending: isPendingFindPeriod,
    isSuccess: isSuccessFindPeriod,
    data: dataFindPeriod,
    isError: isErrorFindPeriod,
    error: errorFindPeriod,
  } = useTransactionFindPeriod({ date: dateStr });

  useEffect(() => {
    if (isSuccessFindPeriod && dataFindPeriod) {
      setPeriod(dataFindPeriod);
      setPreviousDate(undefined);
    }
  }, [isSuccessFindPeriod, dataFindPeriod]);

  const handleSaveError = ({ message, fields }: ApiErrorModel) => {
    if (
      message ===
      "Duplicate Transaction Detected. Please add a unique description or file"
    ) {
      setModalDuplicated(true);
      return;
    } else if (
      message.includes(
        "You cannot create a Leased / HP / Loan transaction because the selected asset is not marked as Leased, Hire Purchase, or",
      )
    ) {
      setModalWrongAsetType(true);
      return;
    }
    Object.keys(fields || {}).forEach((key) =>
      setError(
        key as "businessId" | "categoryId" | "date" | "amount" | "description",
        { message: fields![key] },
      ),
    );
    notify("error", message);
  };

  const {
    mutate: mutateCreateTransaction,
    isPending: isPendingCreateTransaction,
  } = useTransactionCreate((pressedCreateNewBtn) => {
    notify("success", "Transaction created successfully.");
    if (pressedCreateNewBtn) {
      const businessId = defaultBusiness?.uniqueId || businesses[0]?.uniqueId;
      setBusinessId(businessId);
      setCategory(undefined);
      setAssetId("");
      const date = new Date();
      setDate(date);
      reset({
        businessId,
        categoryId: "",
        assetId: "",
        date,
        isDescriptionRequired: false,
        isAssetRequired: false,
        amount: "",
        description: "",
      });
      setFiles([]);
    }
    navigate(`/transactions${pressedCreateNewBtn ? "/create" : ""}`);
  }, handleSaveError);

  const {
    mutate: mutateUpdateTransaction,
    isPending: isPendingUpdateTransaction,
  } = useTransactionUpdate((pressedCreateNewBtn) => {
    notify("success", "Transaction updated successfully.");
    navigate(`/transactions${pressedCreateNewBtn ? "/create" : ""}`);
  }, handleSaveError);

  useEffect(() => {
    setState({
      isLoading:
        (isStaleGetOne && isPendingGetOne) ||
        isPendingGetCategories ||
        isPendingGetAssets ||
        isPendingCreateTransaction ||
        isPendingUpdateTransaction ||
        isPendingFindPeriod ||
        isPendingBulkCreate,
    });
  }, [
    isStaleGetOne,
    isPendingGetOne,
    isPendingGetCategories,
    isPendingGetAssets,
    isPendingCreateTransaction,
    isPendingUpdateTransaction,
    isPendingFindPeriod,
    isPendingBulkCreate,
    setState,
  ]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    setError,
    setValue,
    getValues,
    trigger,
  } = useForm<TransactionFormInterface>({
    mode: "onBlur",
    disabled:
      (isStaleGetOne && isPendingGetOne) ||
      isPendingGetCategories ||
      isPendingGetAssets ||
      isPendingCreateTransaction ||
      isPendingUpdateTransaction ||
      isPendingFindPeriod,
    resolver: yupResolver(transactionSchema),
  });

  const { files, setFiles, isUploading, selectFiles, removeFile } =
    useFileUpload(() => clearErrors("files"));

  const pickAsset = useCallback(
    ({
      categoryIdValue,
      assetId,
      assets = [],
      categories,
      category,
      date,
      period,
    }: {
      categoryIdValue?: string;
      assetId?: string;
      assets?: AssetModel[];
      categories?: CategoriesModel;
      category?: CategorySelectorModel;
      date?: Date;
      period?: PeriodModel;
    }) => {
      const subcat = getSubcategoryById(
        categories,
        categoryIdValue || category?.categoryId,
      );
      const periodStartDateRez = period
        ? new Date(period.startDate)
        : undefined;
      const filteredAssets = filterAssetsByDate({
        assets,
        date,
        startPeriodDate: periodStartDateRez,
        excludeTypes: subcat?.hideFromAssetTypes,
        hideAssetsWithMiles: businessType === "other",
      });
      const defaultAsset = filteredAssets.find(({ isDefault }) => isDefault);
      if (
        assetId &&
        !filteredAssets.find(({ uniqueId }) => uniqueId === assetId)
      ) {
        setAssetId("");
        setValue("assetId", "");
        clearErrors("assetId");
      } else if (!assetId && defaultAsset) {
        setAssetId(defaultAsset.uniqueId);
        setValue("assetId", defaultAsset.uniqueId);
        clearErrors("assetId");
      } else if (
        !assetId &&
        filteredAssets.filter(({ disposal }) => !disposal).length === 1
      ) {
        setAssetId(
          filteredAssets.filter(({ disposal }) => !disposal)[0].uniqueId,
        );
        setValue(
          "assetId",
          filteredAssets.filter(({ disposal }) => !disposal)[0].uniqueId,
        );
        clearErrors("assetId");
      }
    },
    [clearErrors, setValue, businessType],
  );

  useEffect(() => {
    if (category && period?.startDate && assets && !previousDate) {
      setPreviousDate(date);
      const subcat = getSubcategoryById(categories, category.categoryId);
      if (!disabledFeatures.includes("Assets") && subcat?.assetRequired) {
        pickAsset({ assetId, assets, categories, category, date, period });
      }
    }
  }, [
    categories,
    category,
    period,
    pickAsset,
    previousDate,
    date,
    assetId,
    assets,
    disabledFeatures,
  ]);

  useEffect(() => {
    if (isSuccessGetOne && dataGetOne) {
      setBusinessId(dataGetOne.userBusiness.uniqueId);
      setCategory({
        type: dataGetOne.type,
        categoryId: dataGetOne.subcategory.uniqueId,
      });
      setExcludedAssetTypes(dataGetOne.subcategory.hideFromAssetTypes);
      const assetId = dataGetOne.asset?.uniqueId || "";
      setAssetId(assetId);
      const date = !isRefundForm ? new Date(dataGetOne.date) : new Date();
      setDate(date);
      reset({
        businessId: dataGetOne.userBusiness.uniqueId,
        categoryId: dataGetOne.subcategory.uniqueId,
        isDescriptionRequired:
          dataGetOne.subcategory.transactionDescriptionRequired,
        isAssetRequired: disabledFeatures.includes("Assets")
          ? false
          : dataGetOne.subcategory.assetRequired,
        assetId,
        date,
        amount: formatAmountAfterEditing(dataGetOne.amount),
        description: !isRefundForm
          ? dataGetOne.description || ""
          : "Refund for a Transaction",
      });
      if (!isRefundForm) {
        setAttachments(dataGetOne.attachments || []);
      }
    } else {
      const businessId = defaultBusiness?.uniqueId || businesses[0]?.uniqueId;
      setBusinessId(businessId);
      setCategory(undefined);
      setAssetId("");
      const date = new Date();
      setDate(date);
      reset({
        businessId,
        categoryId: "",
        assetId: "",
        date,
        isDescriptionRequired: false,
        isAssetRequired: false,
        amount: "",
        description: "",
      });
      setFiles([]);
    }
  }, [
    isSuccessGetOne,
    dataGetOne,
    disabledFeatures,
    isRefundForm,
    defaultBusiness,
    businesses,
    reset,
    setFiles,
  ]);

  useEffect(() => {
    if (isErrorFindPeriod && errorFindPeriod) {
      setPeriod(undefined);
      setError("date", { message: errorFindPeriod.message });
    }
  }, [isErrorFindPeriod, errorFindPeriod, setError]);

  useEffect(() => {
    if (!modalAssetError && formState.errors.assetId?.message) {
      setModalAssetError(true);
    }
  }, [modalAssetError, formState]);

  const handleSelectBusiness = (businessId: string) => {
    setBusinessId(businessId);
    setValue("businessId", businessId);
    setCategory(undefined);
    setValue("categoryId", "");
    clearErrors("description");
    setAssetId("");
    setValue("assetId", "");
  };

  const handleSelectCategory = (value?: CategorySelectorModel) => {
    setCategory(value);
    setValue("categoryId", value?.categoryId || "");
    clearErrors("categoryId");
    const subcat = getSubcategoryById(categories, value?.categoryId);
    setValue("isDescriptionRequired", subcat?.transactionDescriptionRequired);
    clearErrors("isDescriptionRequired");
    clearErrors("isAssetRequired");
    clearErrors("description");
    if (!disabledFeatures.includes("Assets")) {
      setValue("isAssetRequired", subcat?.assetRequired);
      setExcludedAssetTypes(subcat?.hideFromAssetTypes);
      setAssetId("");
      setValue("assetId", "");
      if (subcat?.assetRequired) {
        pickAsset({
          categoryIdValue: value?.categoryId,
          assetId,
          assets,
          categories,
          category,
          date,
          period,
        });
      }
    }
  };

  const handleSelectAsset = (assetId: string) => {
    setAssetId(assetId);
    setValue("assetId", assetId);
    clearErrors("assetId");
  };

  const handleSelectDate = (value?: Date) => {
    setDate(value || new Date());
    setValue("date", value || new Date());
    clearErrors("date");
  };

  const removeAttachment = (uniqueId: string) => {
    const newAttachments = attachments.filter(
      (attachment) => attachment.uniqueId !== uniqueId,
    );
    setAttachments(newAttachments);
  };

  const validateBulkSharedFields = async (): Promise<
    TransactionBulkSharedValues | undefined
  > => {
    const isValid = await trigger([
      "businessId",
      "categoryId",
      "assetId",
      "date",
    ]);
    if (isErrorFindPeriod && errorFindPeriod) {
      setError("date", { message: errorFindPeriod.message });
      return;
    }
    if (!isValid || !period) {
      return;
    }
    const { businessId, categoryId, assetId, date } = getValues();
    return { businessId, categoryId, assetId, date: new Date(date) };
  };

  const onSubmit: SubmitHandler<TransactionFormInterface> = (data) => {
    const amount = data.amount.replaceAll(/[^0-9/.]*/g, "") || "0";
    const violation = getAmountLimitViolation(amount, subcategory, {
      softMax: confirmSoftMaxAmount,
      softMin: confirmSoftMinAmount,
    });
    setLimitViolation(violation);
    if (violation) {
      return;
    }

    const transactionAmount = dataGetOne?.amount
      ? parseFloat(dataGetOne?.amount)
      : 0;
    if (
      isRefundForm &&
      idValue !== "refund" &&
      parseFloat(amount) > transactionAmount
    ) {
      setConfirmBiggerAmount(true);
      return;
    }

    return idValue && !["refund", "create"].includes(idValue) && !isRefundForm
      ? mutateUpdateTransaction({
          ...data,
          transactionId: idValue,
          attachments,
          files,
          amount,
        })
      : mutateCreateTransaction({
          ...data,
          refund: isRefundForm,
          refundOf: isRefundForm && idValue !== "refund" ? idValue : undefined,
          files,
          amount,
        });
  };

  const confirmSubmit = () => {
    setConfirmBiggerAmount(false);
    const data = getValues();
    mutateCreateTransaction({
      ...data,
      date: new Date(data.date),
      refund: true,
      refundOf: idValue,
      files,
      amount: data.amount.replaceAll(/[^0-9/.]*/g, "") || "0",
    });
  };

  const handleCloseModalAssetError = () => {
    setModalAssetError(false);
    clearErrors("assetId");
  };

  return (
    <div className="min-h-dvh bg-white-100 md:min-h-0 md:bg-lightGray">
      {isMobile && (
        <MobileHeader
          href="/transactions"
          title={
            (isRefundForm && "Transaction Refund") ||
            (dataGetOne && isSuccessGetOne && "Edit Transaction") ||
            "Add Transaction"
          }
        />
      )}
      {!isMobile && (
        <div className="mt-4 flex h-16 items-center gap-x-2 bg-lightGray pr-4 md:mt-0 md:h-24 md:pr-8">
          <Link
            to="/transactions"
            className="flex h-8 w-8 items-center justify-center rounded-e-lg border border-black-8 bg-white-100 text-primary-300"
          >
            <ChevronLeftSvg />
          </Link>
          <h1 className="text-400 font-bold text-black-100 md:text-600">
            {(isRefundForm && "Transaction Refund") ||
              (dataGetOne && isSuccessGetOne && "Edit Transaction") ||
              "Add Transaction"}
          </h1>
        </div>
      )}
      <Form
        onSubmit={
          isBulkMode
            ? (event) => {
                event.preventDefault();
                bulkDetailsRef.current?.submit();
              }
            : handleSubmit(onSubmit)
        }
        className="px-4 text-black-100 md:min-h-[calc(100dvh_-_248px)] md:rounded-b-lg md:rounded-t-3xl md:px-10"
      >
        <div
          className={clsx(
            "flex flex-col",
            !isBulkMode && "md:flex-row md:gap-x-10",
          )}
        >
          <div
            className={
              isBulkMode
                ? // Same width as one card of the bulk grid (gap-4 = 1rem)
                  "md:w-[calc((100%_-_1rem)/2)] xl:w-[calc((100%_-_2rem)/3)]"
                : "md:w-[432px]"
            }
          >
            <input
              {...register<"pressedCreateNewBtn">("pressedCreateNewBtn")}
              type="hidden"
              name="pressedCreateNewBtn"
            />
            <FormRow
              top={isMobile ? "large" : "medium"}
              className="text-300 font-bold md:text-400"
            >
              Transaction information
            </FormRow>
            <FormRow top={isMobile ? "small" : "large"}>
              <BusinessSelector
                value={businessId}
                onSetBusiness={handleSelectBusiness}
                variant="formField"
                dataTestid="input_businessId"
              />
              <input
                {...register<"businessId">("businessId")}
                type="hidden"
                name="businessId"
              />
            </FormRow>
            <FormRow top="small">
              <CategorySelector
                categories={categories}
                value={category}
                dataTestid="input_categoryId"
                onSetCategory={handleSelectCategory}
                error={formState.errors.categoryId?.message}
              />
              <input
                {...register<"categoryId">("categoryId")}
                type="hidden"
                name="categoryId"
              />
            </FormRow>
            {!disabledFeatures.includes("Assets") && (
              <FormRow
                top="small"
                className={clsx(
                  !getSubcategoryById(categories, category?.categoryId)
                    ?.assetRequired && "hidden",
                )}
              >
                <AssetSelector
                  businessType={businessType}
                  items={assets || []}
                  date={date}
                  startPeriodDate={
                    period ? new Date(period.startDate) : undefined
                  }
                  excludeTypes={excludedAssetTypes}
                  hideAssetsWithMiles
                  value={assetId}
                  error={formState.errors.assetId?.message}
                  onSetAsset={handleSelectAsset}
                />
                <input
                  {...register<"assetId">("assetId")}
                  type="hidden"
                  name="assetId"
                />
                <input
                  {...register<"isAssetRequired">("isAssetRequired")}
                  type="hidden"
                  name="isAssetRequired"
                />
              </FormRow>
            )}
            <FormRow top="small">
              <DatePicker
                date={date}
                prefix="transactionForm"
                dataTestid="input_date"
                onSetDate={handleSelectDate}
                error={formState.errors.date?.message}
              />
              <input {...register<"date">("date")} type="hidden" name="date" />
              {period && (
                <div className="border-l-4 border-l-primary-300 bg-primary-104 px-4 py-2">
                  <div className="text-100 text-black-54">Period</div>
                  <div className="pt-2 text-black-100">{period.title}</div>
                </div>
              )}
            </FormRow>
          </div>
          {isBulkMode ? (
            <TransactionBulkDetails
              ref={bulkDetailsRef}
              subcategory={subcategory}
              onValidateShared={validateBulkSharedFields}
              onSaveError={handleSaveError}
              onCountChange={setBulkCount}
            />
          ) : (
            <div className="md:w-[432px]">
              <FormRow
                top={isMobile ? "large" : "medium"}
                className="text-300 font-bold md:text-400"
              >
                Transaction details
              </FormRow>
              <FormRow top={isMobile ? "small" : "large"}>
                <FormInput
                  {...register<"amount">("amount")}
                  name="amount"
                  label="Amount"
                  type="text"
                  maxLength={13}
                  data-testid="input_amount"
                  error={formState.errors.amount?.message}
                  onChange={({ currentTarget }) => {
                    setValue("amount", currentTarget.value);
                    clearErrors("amount");
                  }}
                  onBlur={({ currentTarget }) =>
                    setValue("amount", formatAmountOnBlur(currentTarget.value))
                  }
                />
              </FormRow>
              <FormRow top="small">
                <FormText
                  {...register<"description">("description")}
                  name="description"
                  label="Description"
                  data-testid="input_description"
                  error={formState.errors.description?.message}
                  onChange={() => clearErrors("description")}
                  className="min-h-[136px]"
                />
                <input
                  {...register<"isDescriptionRequired">(
                    "isDescriptionRequired",
                  )}
                  type="hidden"
                  name="isDescriptionRequired"
                />
              </FormRow>
            </div>
          )}
        </div>
        {!isBulkMode && (
          <>
            <FormRow top="large" className="text-300 font-bold md:text-400">
              Transaction Receipts
            </FormRow>
            <FormRow top={isMobile ? "small" : "large"}>
              <TransactionReceipts
                name="files"
                dataTestid="input_files"
                files={files}
                attachments={isRefundForm ? [] : attachments}
                uploading={isUploading}
                error={formState.errors.files?.message}
                className="max-w-[430px]"
                onSelectFiles={selectFiles}
                onRemoveFile={removeFile}
                onRemoveAttachment={removeAttachment}
              />
            </FormRow>
          </>
        )}
        {!isMobile && (
          <div className="-mx-10 mt-10 h-px w-[calc(100%_+_80px)] bg-black-8" />
        )}
        <FormRow
          isRow={!isMobile}
          isBetween
          top="small"
          className="gap-y-4 pb-6"
        >
          <div
            className="flex flex-col gap-4 md:flex-row"
            data-testid="buttons_saveTransaction"
          >
            <Button
              type="submit"
              title={
                (isBulkMode &&
                  `Save ${bulkCount} Transaction${bulkCount > 1 ? "s" : ""}`) ||
                (isRefundForm && "Save Refund") ||
                (dataGetOne && isSuccessGetOne && "Update") ||
                "Save Transaction"
              }
              leftIcon={
                dataGetOne && isSuccessGetOne ? (
                  <DeviceFloppySvg />
                ) : (
                  <SquareRoundedPlusSvg />
                )
              }
              className="w-full md:w-fit md:px-5"
              height="small"
              dataTestid="button_saveTransaction"
              disabled={
                !isRefundForm &&
                dataGetOne &&
                isSuccessGetOne &&
                ["submitted", "completed"].includes(dataGetOne.status.value)
              }
            />
            {!isRefundForm && !isBulkMode && (
              <Button
                type="submit"
                title="Save and Add Another"
                leftIcon={<CheckupListSvg />}
                height="small"
                variant="secondary"
                onClick={() => setValue("pressedCreateNewBtn", true)}
              />
            )}
          </div>
          <Button
            title="Cancel"
            variant="secondary"
            height="small"
            leftIcon={<XSvg />}
            isLink
            href="/transactions"
          />
        </FormRow>
        <SoftAmountLimitModal
          violation={limitViolation}
          softMaxMessage={subcategory?.softMaxAmountMessage}
          softMinMessage={subcategory?.softMinAmountMessage}
          confirmButtonType="submit"
          onCancel={() => setLimitViolation(undefined)}
          onConfirm={(confirmed) =>
            confirmed === "softMax"
              ? setConfirmSoftMaxAmount(true)
              : setConfirmSoftMinAmount(true)
          }
        />
      </Form>
      {isRefundForm && (
        <ConfirmModal
          show={confirmBiggerAmount}
          confirmButtonTitle="Yes"
          text1="Are you sure?"
          text2="The Refund Amount you've entered exceeds the Transaction Amount. Do you want to proceed?"
          onClickCancel={() => setConfirmBiggerAmount(false)}
          onClickConfirm={confirmSubmit}
        />
      )}
      {assetId === "new" && (
        <AssetForm
          initialBusinessId={businessId}
          excludeTypes={excludedAssetTypes}
          onClose={() => {
            setAssetId("");
            setValue("assetId", "");
            clearErrors("assetId");
          }}
          onCreated={handleSelectAsset}
        />
      )}
      <DuplicatedTransactionModal
        show={modalDuplicated}
        onClose={() => setModalDuplicated(false)}
      />
      <AssetRequiredModal
        show={modalAssetError}
        onClose={handleCloseModalAssetError}
      />
      <WrongAssetTypeModal
        show={modalWrongAsetType}
        onClose={() => setModalWrongAsetType(false)}
      />
      <HardMaxAmountModal
        show={limitViolation === "hardMax"}
        message={subcategory?.hardMaxAmountMessage}
        onClose={() => setLimitViolation(undefined)}
      />
    </div>
  );
};
